import React, { useState } from 'react';
import {
  withStyles,
  Modal,
} from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  setShaftValues,
  deleteShaftItem,
  searchShaftList,
  restoreOriginalShaft,
  createShaftItem,
  updateShaftList,
} from '~/store/dailyparts/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate, formatTime, newMomentDate, parseDate, parseDateTimeToTimestamp, subtractDaysFromNow,
} from '~/utils/moment';
import EditHistoryButton from '~/components/EditHistoryButton';
import usePlatformConfiguration, { usePlatformConfigurationUserAccessLevel } from '~/services/platformConfiguration';
import { usePagination } from '../hooks';

const SHAFT_EQUIP_TYPES = [20];

const ShaftList = () => {
  const { t: translate } = useTranslation();

  const shaftList = useSelector(state => state.dailyParts.shaftList);
  const filters = useSelector(state => state.dailyParts.filters);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const shaftGroups = useSelector(
    state => state.manager.equipmentsGroups
      .filter(r => SHAFT_EQUIP_TYPES.includes(r.id_equipament)), shallowEqual,
  );
  const shaftEquips = useSelector(
    state => state.manager.equipments
      .filter(r => SHAFT_EQUIP_TYPES.includes(r.id_equip)), shallowEqual,
  );
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators.filter(o => o.name), shallowEqual);
  const materials = useSelector(state => state.manager.materials);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const operationTypes = useSelector(state => state.manager.operationTypes);
  const originalShaftList = useSelector(state => state.dailyParts.originalShaftList);
  const shaftPagination = useSelector(
    state => state.dailyParts.pagination.shaftPagination,
  );

  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const closures = useSelector(state => state.manager.closures);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const dispatch = useDispatch();

  const isDateEditable = (rowData, key) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (enableClosure && closures?.length > 0) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig
        : lockEditLastClosure.add(1, 'day');
    }

    if (rowData[key] < userLimitEdit) {
      return { isValid: false, helperText: translate('validation:InvalidDate') };
    }

    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || currentDatetime.format('DD/MM/YYYY');
    const stTime = rowData.start_time || currentDatetime.format('HH:mm:ss');
    const endDate = rowData.end_date || currentDatetime.format('DD/MM/YYYY');
    const endTime = rowData.end_time || currentDatetime.format('HH:mm:ss');

    const startDateTimestamp = parseDateTimeToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateTimeToTimestamp(endDate, endTime);

    if (key.startsWith('end_')) {
      if (endDateTimestamp < startDateTimestamp) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (endDateTimestamp > currentDatetime.unix() * 1000) {
        return {
          isValid: false,
          helperText: translate('validation:FutureDateCantBeSelected'),
        };
      }
    }

    return { isValid: true };
  };

  const sortElements = list => list
    .sort((i, k) => (i.name < k.name ? 1 : -1))
    .sort(i => i.active * -1);

  const withInactiveLabel = list => list.map(e => ({
    ...e,
    name: e.active ? e.name : `${e.name} - Inativo`,
  }));

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'start_date'),
    },
    {
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'end_date'),
    },
    {
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
    },
    {
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      customType: 'number',
    },
    {
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
      customType: 'filterLookup',
    },
    {
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
    },
    {
      title: translate('common:OperationType'),
      field: 'id_operation_type',
      lookup: operationTypes.filter(
        r => r.id_equipment_type === 20,
      ),
      customType: 'filterLookup',
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        operator_id: '',
      }),
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group)
        === String(rowData.operator_group_id),
    },
    {
      title: translate('common:Material'),
      field: 'material_id',
      lookup: materials,
      customType: 'filterLookup',
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equipament_groups_id',
      lookup: shaftGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        group_equipament_links_id: '',
      }),
    },
    {
      title: translate('common:Equipment'),
      field: 'group_equipament_links_id',
      lookup: shaftEquips,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group)
        === String(rowData.equipament_groups_id),
    },
    {
      title: translate('common:Origin'),
      field: 'id_mine',
      lookup: withInactiveLabel(sortElements(elements)),
      customType: 'filterLookup',
      customOnChange: () => ({
        id_mine_origin: '',
      }),
    },
    {
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      lookup: withInactiveLabel(sortElements(subElements)),
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_element) === String(rowData.id_mine),
    },
    {
      title: translate('common:LoadManager'),
      field: 'load_manager',
      customType: 'text',
    },
    {
      title: translate('common:LoadTime'),
      field: 'load_time',
      customType: 'number',
    },
    {
      title: translate('common:IdleTime'),
      field: 'idle_time',
      customType: 'number',
    },
    {
      title: translate('common:CodeTime'),
      field: 'code_time',
      customType: 'number',
    },
    {
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
    },
  ];

  const customEditHistoryColumns = [
    {
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
    },
    {
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
    },
    {
      title: translate('common:OperationType'),
      field: 'id_operation_type',
      lookup: operationTypes.filter(r => r.id_equipment_type === 20),
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
    },
    {
      title: translate('common:Material'),
      field: 'material_id',
      lookup: materials,
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equipament_groups_id',
      lookup: shaftGroups.filter(e => e.id_equipament === filters.equipmentType),
    },
    {
      title: translate('common:Equipment'),
      field: 'group_equipament_links_id',
      lookup: shaftEquips.filter(e => e.id_equip === filters.equipmentType),
    },
    {
      title: translate('common:Origin'),
      field: 'id_mine',
      lookup: elements,
    },
    {
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      lookup: subElements,
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
    },
  ];

  const filterToFormFieldMap = {
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
    equipmentGroup: 'equipament_groups_id',
    equipment: 'group_equipament_links_id',
    equipmentType: 'equip_type',
    startOrigin: 'id_mine',
    startSubOrigin: 'id_mine_origin',
    material: 'material_id',
    turn: 'turn_id',
  };

  const convertIfNeeded = (value, key) => {
    if (key === 'codeType' || key === 'codeGroup') {
      return Number(value);
    }
    return value;
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValue = filters[filterKey];

      if (filterValue !== undefined && filterValue !== 0) {
        if (Array.isArray(filterValue) && filterValue.length === 1) {
          const [firstValue] = filterValue;
          if (firstValue !== undefined) {
            acc[formField] = convertIfNeeded(firstValue, filterKey);
          }
        } else {
          acc[formField] = convertIfNeeded(filterValue, filterKey);
        }
      }

      return acc;
    },
    {},
  );

  const initialFormData = {
    start_date: formatDate(newMomentDate(), true),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    end_date: formatDate(newMomentDate(), true),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    duration: 0,
    turn_id: '',
    team_id: '',
    operator_group_id: '',
    operator_id: '',
    material_id: '',
    equipament_groups_id: '',
    group_equipament_links_id: '',
    load_manager: 0,
    load_time: 0,
    idle_time: 0,
    code_time: '',
    id_operation_type: '',
    id_mine: '',
    id_mine_origin: '',
  };

  const removeUnnecessaryFields = (data) => {
    const result = { ...data };
    const fieldsToRemove = [
      'startDate',
      'endDate',
      'turn',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'material',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
    ];

    fieldsToRemove.forEach((field) => {
      delete result[field];
    });

    return result;
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'shaft',
    pagination: shaftPagination,
    searchAction: searchShaftList,
  });

  const { page = 0, total = 0, loadingMore = false } = shaftPagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        columns={applyDefaultSort(columns)}
        data={shaftList}
        initialFormData={initialFormData}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          selection: true,
          search: true,
          cacheKey: 'shaft_list',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={(newItem) => {
          const createParams = {
            ...newItem,
            ...mappedFilterData,
          };
          dispatch(createShaftItem(createParams));
        }}
        onDiscard={() => dispatch(restoreOriginalShaft())}
        onApply={() => {
          const parsedData = shaftList
            .filter(item => item.update_status)
            .map(removeUnnecessaryFields);

          const actionData = {
            data: parsedData,
            parameters: filters,
            saveLog: true,
            ogData: originalShaftList,
          };
          dispatch(updateShaftList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach(({ ...row }) => {
            dispatch(setShaftValues({ ...row, touched: true }));
          });
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deleteShaftItem(e));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="shaft"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

ShaftList.defaultProps = {};

export default withStyles(styles)(ShaftList);
